import { type ResultOf } from '@graphql-typed-document-node/core';
import { useToast } from '@medely/ui-kit';
import { gateNames } from '@mp/constants';
import { graphql } from '@mp/graphql/generated';
import { useCurrentUser } from '@mp/hooks';
import { useGate } from './useGate';
import { useGraphQLQuery } from './useGraphQLRequest';
import { useMissingConditionsCg } from './useMissingConditionsCg';

const RecommendedCredentialsJobCountsSummaryGql = graphql(`
  query RecommendedCredentialsJobCountsSummary {
    recommendedCredentialsJobCounts {
      open_jobs_count
      recently_posted_jobs_count
      conditions {
        condition {
          id
        }
      }
    }
  }
`);

type RecommendedCredentialsJobCountsSummaryGqlResult = ResultOf<
  typeof RecommendedCredentialsJobCountsSummaryGql
>;

type UseRecommendedCredentialsJobCountsSummaryResult = {
  isEnabled: boolean;
  conditionCount: number;
  openJobsCount: number;
  recentlyPostedJobsCount: number;
  isLoading: boolean;
  refetch: () => Promise<unknown>;
};

export const useRecommendedCredentialsJobCountsSummary =
  (): UseRecommendedCredentialsJobCountsSummaryResult => {
    const isEnabled = useGate(gateNames.recommendedCredentialsDemand);
    const { currentUser } = useCurrentUser();
    const toast = useToast();
    const onError = () => toast.error('Sorry, something went wrong');

    const enabled = isEnabled && !!currentUser?.professional;

    const { data, isError, isLoading, isInitialLoading, refetch } =
      useGraphQLQuery<RecommendedCredentialsJobCountsSummaryGqlResult>({
        enabled,
        onError,
        operationName: 'RecommendedCredentialsJobCountsSummary',
        query: RecommendedCredentialsJobCountsSummaryGql,
        variables: {},
      });

    const currentRecommended = useMissingConditionsCg({
      recommended: true,
      enabled: !isEnabled,
      onError,
    });

    if (!isEnabled) {
      return {
        isEnabled,
        conditionCount: currentRecommended.isError ? 0 : currentRecommended.conditions.length,
        openJobsCount: 0,
        recentlyPostedJobsCount: 0,
        isLoading: currentRecommended.isLoading,
        refetch: currentRecommended.refetchMissingConditions,
      };
    }

    const jobCounts = isError ? undefined : data?.recommendedCredentialsJobCounts;

    return {
      isEnabled,
      conditionCount: jobCounts?.conditions.length ?? 0,
      openJobsCount: jobCounts?.open_jobs_count ?? 0,
      recentlyPostedJobsCount: jobCounts?.recently_posted_jobs_count ?? 0,
      isLoading: enabled ? isLoading : isInitialLoading,
      refetch,
    };
  };
